import React, { useState, useEffect, useCallback } from 'react';
import { cleanProceedingName } from '../lib/cleanProceedingName';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, BellOff, Trash2, Loader, Gavel, Search, ExternalLink, Sparkles, Plus, Mail, FileText, Settings2, ChevronDown, ChevronRight, History } from 'lucide-react';
import {
  docketFollowService,
  type FollowedDocketWithMeta,
  type DigestFrequency,
} from '../lib/docketFollowService';
import {
  getDigestPreview,
  getDigestHistory,
  getDigestDeliveryFilings,
  type DigestPreview,
  type DigestPreviewDocket,
  type DigestHistoryEntry,
  type DigestDeliveryFiling,
} from '../lib/watchlistDigestService';
import {
  getDigestPreferences,
  upsertDigestPreferences,
  type UserDigestPreferences,
} from '../lib/userDigestPreferencesService';
import { SimpleDocketAutocomplete } from '../components/SimpleDocketAutocomplete';
import { supabase } from '../lib/supabase';

const FREQ_LABELS: Record<DigestFrequency, string> = {
  immediate: 'Immediate',
  daily: 'Daily',
  weekly: 'Weekly',
  none: 'Off',
};

const FREQ_COLORS: Record<DigestFrequency, string> = {
  immediate: 'bg-rose-100 text-rose-700 border-rose-200',
  daily: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  weekly: 'bg-sky-100 text-sky-700 border-sky-200',
  none: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

export const WatchlistPage: React.FC = () => {
  const navigate = useNavigate();
  const [follows, setFollows] = useState<FollowedDocketWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingFreq, setEditingFreq] = useState<string | null>(null);

  const fetchFollows = useCallback(async () => {
    try {
      setError(null);
      const data = await docketFollowService.getFollowedDockets();
      setFollows(data);
    } catch (err) {
      console.error('Error loading watchlist:', err);
      setError('Failed to load your watchlist. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFollows();
  }, [fetchFollows]);

  const handleFollow = async (docketNumber: string) => {
    try {
      setActionLoading(docketNumber);
      await docketFollowService.follow(docketNumber);
      setSearchValue('');
      await fetchFollows();
    } catch (err) {
      console.error('Error following docket:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnfollow = async (docketNumber: string) => {
    try {
      setActionLoading(docketNumber);
      await docketFollowService.unfollow(docketNumber);
      setFollows(prev => prev.filter(f => f.docket_number !== docketNumber));
    } catch (err) {
      console.error('Error unfollowing docket:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleFrequencyChange = async (
    docketNumber: string,
    frequency: DigestFrequency
  ) => {
    try {
      await docketFollowService.updateDigestFrequency(docketNumber, frequency);
      setFollows(prev =>
        prev.map(f =>
          f.docket_number === docketNumber
            ? { ...f, digest_frequency: frequency, notify_new_filings: frequency !== 'none' }
            : f
        )
      );
      setEditingFreq(null);
    } catch (err) {
      console.error('Error updating frequency:', err);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const followedNumbers = new Set(follows.map(f => f.docket_number));

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 border border-emerald-200">
            <BellRing className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Watchlist
            </h1>
            <p className="text-sm text-gray-500">
              Follow proceedings to get alerts when new filings are submitted.
            </p>
          </div>
        </div>
      </div>

      {/* Search to add */}
      <div className="mb-8">
        <div className="mb-2 block text-sm font-medium text-gray-700">
          Add a proceeding to your watchlist
        </div>
        <SimpleDocketAutocomplete
          value={searchValue}
          onChange={setSearchValue}
          onSubmit={(val) => {
            if (val && !followedNumbers.has(val)) {
              handleFollow(val);
            } else if (followedNumbers.has(val)) {
              navigate(`/proceedings/${encodeURIComponent(val)}`);
            }
          }}
          placeholder="Search by docket number or description (e.g., 21-402, net neutrality)"
        />
        {searchValue && followedNumbers.has(searchValue) && (
          <p className="mt-2 text-sm text-emerald-600">
            You're already following {searchValue}.
          </p>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700">
          {error}
          <button
            onClick={() => { setLoading(true); fetchFollows(); }}
            className="ml-3 font-medium underline"
          >
            Retry
          </button>
        </div>
      ) : follows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <Bell className="mx-auto mb-4 h-10 w-10 text-gray-300" />
          <h3 className="mb-2 text-lg font-semibold text-gray-700">
            No proceedings on your watchlist
          </h3>
          <p className="mb-6 text-sm text-gray-500">
            Search above to find dockets relevant to your practice, or browse{' '}
            <button
              onClick={() => navigate('/proceedings')}
              className="font-medium text-emerald-600 hover:text-emerald-700 underline"
            >
              Proceedings
            </button>{' '}
            to discover active proceedings.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>
              {follows.length} proceeding{follows.length !== 1 ? 's' : ''} on your watchlist
            </span>
          </div>

          {follows.map((follow) => (
            <div
              key={follow.id}
              className="group relative rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-sm"
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 group-hover:bg-emerald-50 group-hover:text-emerald-600 transition-colors">
                  <Gavel className="h-4 w-4" />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <button
                        onClick={() =>
                          navigate(
                            `/proceedings/${encodeURIComponent(follow.docket_number)}`
                          )
                        }
                        className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors text-left"
                      >
                        {follow.docket_number}
                        <ExternalLink className="ml-1.5 inline h-3 w-3 opacity-0 group-hover:opacity-50" />
                      </button>
                      {follow.proceeding_name && (
                        <p className="mt-0.5 text-sm text-gray-500 line-clamp-1">
                          {cleanProceedingName(follow.proceeding_name)}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex shrink-0 items-center gap-2">
                      {/* Frequency badge / editor */}
                      <div className="relative">
                        <button
                          onClick={() =>
                            setEditingFreq(
                              editingFreq === follow.docket_number
                                ? null
                                : follow.docket_number
                            )
                          }
                          className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                            FREQ_COLORS[follow.digest_frequency as DigestFrequency]
                          }`}
                          title="Change alert frequency"
                        >
                          {follow.notify_new_filings ? (
                            <>{FREQ_LABELS[follow.digest_frequency as DigestFrequency]} alerts</>
                          ) : (
                            <>Alerts off</>
                          )}
                        </button>

                        {editingFreq === follow.docket_number && (
                          <div className="absolute right-0 top-8 z-20 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                            {(
                              ['immediate', 'daily', 'weekly', 'none'] as DigestFrequency[]
                            ).map((freq) => (
                              <button
                                key={freq}
                                onClick={() =>
                                  handleFrequencyChange(follow.docket_number, freq)
                                }
                                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${
                                  follow.digest_frequency === freq
                                    ? 'font-medium text-emerald-700'
                                    : 'text-gray-700'
                                }`}
                              >
                                {freq === 'none' ? (
                                  <BellOff className="h-3.5 w-3.5" />
                                ) : (
                                  <Bell className="h-3.5 w-3.5" />
                                )}
                                {FREQ_LABELS[freq]}
                                {follow.digest_frequency === freq && (
                                  <span className="ml-auto text-emerald-500">&#10003;</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Unfollow */}
                      <button
                        onClick={() => handleUnfollow(follow.docket_number)}
                        disabled={actionLoading === follow.docket_number}
                        className="rounded-lg p-1.5 text-gray-400 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 disabled:opacity-50"
                        title="Unfollow"
                      >
                        {actionLoading === follow.docket_number ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Meta row */}
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                    <span>Followed {formatDate(follow.followed_at)}</span>
                    {follow.status && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          follow.status === 'open'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-neutral-100 text-neutral-500'
                        }`}
                      >
                        {follow.status === 'open' ? 'Active' : follow.status}
                      </span>
                    )}
                    {follow.bureau && (
                      <span className="text-gray-400">{follow.bureau}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Digest preview */}
      {follows.length > 0 && !loading && (
        <UpcomingDigestPreview />
      )}

      {/* Digest preferences */}
      {follows.length > 0 && !loading && (
        <DigestPreferencesSection />
      )}

      {/* Digest history */}
      {follows.length > 0 && !loading && (
        <DigestHistorySection />
      )}

      {/* Suggested dockets */}
      <SuggestedDockets
        followedNumbers={followedNumbers}
        onFollow={handleFollow}
        actionLoading={actionLoading}
      />
    </div>
  );
};

// ── Digest Preferences ─────────────────────────────────────────────────────

const DigestPreferencesSection: React.FC = () => {
  const [prefs, setPrefs] = useState<UserDigestPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDigestPreferences()
      .then(setPrefs)
      .catch(() => setError('Could not load preferences'))
      .finally(() => setLoading(false));
  }, []);

  const update = async (
    field: 'digests_enabled' | 'skip_weekends' | 'max_filings_per_docket',
    value: boolean | number
  ) => {
    if (!prefs) return;
    const previous = prefs;
    setPrefs({ ...prefs, [field]: value } as UserDigestPreferences);
    setSaving(field);
    setError(null);
    try {
      const updated = await upsertDigestPreferences({ [field]: value });
      setPrefs(updated);
    } catch (err) {
      console.error('Error saving digest preferences:', err);
      setPrefs(previous);
      setError('Could not save. Please try again.');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-gray-400">
        <Loader className="h-3.5 w-3.5 animate-spin" />
        Loading preferences...
      </div>
    );
  }

  if (!prefs) return null;

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <Settings2 className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-700">Digest preferences</h3>
      </div>

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800">
              Digest emails
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              When off, no digest emails are sent regardless of per-docket settings.
            </p>
          </div>
          <ToggleSwitch
            checked={prefs.digests_enabled}
            onChange={(v) => update('digests_enabled', v)}
            disabled={saving === 'digests_enabled'}
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800">
              Skip weekends
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Hold Saturday and Sunday filings for Monday's digest.
            </p>
          </div>
          <ToggleSwitch
            checked={prefs.skip_weekends}
            onChange={(v) => update('skip_weekends', v)}
            disabled={saving === 'skip_weekends' || !prefs.digests_enabled}
          />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-800">
              Maximum filings per docket
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Keeps digests scannable. Overflow is summarized as "… and N more filings".
            </p>
          </div>
          <select
            value={prefs.max_filings_per_docket}
            onChange={(e) => update('max_filings_per_docket', Number(e.target.value))}
            disabled={saving === 'max_filings_per_docket' || !prefs.digests_enabled}
            className="shrink-0 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-800 disabled:opacity-50"
          >
            {[5, 10, 15, 20, 30, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <p className="mt-3 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};

const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1 disabled:opacity-50 ${
      checked ? 'bg-emerald-500' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-1'
      }`}
    />
  </button>
);

// ── Digest History ─────────────────────────────────────────────────────────

const DigestHistorySection: React.FC = () => {
  const [entries, setEntries] = useState<DigestHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    getDigestHistory(10)
      .then((res) => setEntries(res.emails))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mt-6 flex items-center gap-2 text-sm text-gray-400">
        <Loader className="h-3.5 w-3.5 animate-spin" />
        Loading digest history...
      </div>
    );
  }

  if (error || entries.length === 0) return null;

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <History className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-700">Recent digests</h3>
      </div>
      <ul className="divide-y divide-gray-100">
        {entries.map((entry) => (
          <li key={entry.id}>
            <button
              type="button"
              onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              className="flex w-full items-center justify-between gap-3 py-2.5 text-left transition-colors hover:bg-gray-50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm text-gray-800">
                  <span className="font-medium">
                    {new Date(entry.sent_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                  <StatusPill status={entry.status} />
                </div>
                <p className="mt-0.5 text-xs text-gray-500">
                  {entry.total_filings_reported} filing{entry.total_filings_reported !== 1 ? 's' : ''}
                  {entry.dockets_included.length > 0 && (
                    <> across {entry.dockets_included.length} docket{entry.dockets_included.length !== 1 ? 's' : ''}</>
                  )}
                </p>
              </div>
              {expanded === entry.id ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
              )}
            </button>
            {expanded === entry.id && <DigestHistoryDetail entryId={entry.id} />}
          </li>
        ))}
      </ul>
    </div>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    sent: 'bg-blue-50 text-blue-700',
    delivered: 'bg-emerald-50 text-emerald-700',
    bounced: 'bg-red-50 text-red-700',
    failed: 'bg-red-50 text-red-700',
    skipped: 'bg-gray-100 text-gray-500',
  };
  const cls = styles[status] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${cls}`}>
      {status}
    </span>
  );
};

const DigestHistoryDetail: React.FC<{ entryId: string }> = ({ entryId }) => {
  const [filings, setFilings] = useState<DigestDeliveryFiling[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getDigestDeliveryFilings(entryId)
      .then((res) => {
        if (!cancelled) setFilings(res);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  if (loading) {
    return (
      <div className="px-3 pb-3 text-xs text-gray-400">
        <Loader className="inline h-3 w-3 animate-spin" /> Loading filings...
      </div>
    );
  }

  if (filings.length === 0) {
    return (
      <div className="px-3 pb-3 text-xs text-gray-400">
        No filing-level records available for this digest.
      </div>
    );
  }

  // Group filings by docket for a tidy display that mirrors the email layout.
  const byDocket = new Map<string, DigestDeliveryFiling[]>();
  for (const f of filings) {
    const arr = byDocket.get(f.docket_number) || [];
    arr.push(f);
    byDocket.set(f.docket_number, arr);
  }

  return (
    <div className="mb-3 space-y-3 rounded-lg bg-gray-50 p-3">
      {Array.from(byDocket.entries()).map(([docket, items]) => (
        <div key={docket}>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Docket {docket}
          </div>
          <ul className="space-y-1.5">
            {items.map((f) => (
              <li key={f.filing_id} className="rounded border border-gray-200 bg-white p-2 text-xs">
                <div className="flex items-center gap-2">
                  <FileText className="h-3 w-3 shrink-0 text-gray-400" />
                  <span className="font-medium text-gray-800">
                    {f.filer_name || 'Unknown filer'}
                  </span>
                  {f.filing_type && (
                    <span className="text-gray-400">· {f.filing_type}</span>
                  )}
                  {f.date_received && (
                    <span className="ml-auto text-gray-400">
                      {new Date(f.date_received).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
                {f.summary && (
                  <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-gray-600">
                    {f.summary}
                  </p>
                )}
                {f.submission_id && (
                  <a
                    href={`https://www.fcc.gov/ecfs/search/search-filings/filing/${f.submission_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-green-700 hover:underline"
                  >
                    <ExternalLink className="h-2.5 w-2.5" />
                    View on ECFS
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

// ── Upcoming Digest Preview ────────────────────────────────────────────────

const UpcomingDigestPreview: React.FC = () => {
  const [preview, setPreview] = useState<DigestPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(false);
    getDigestPreview()
      .then(setPreview)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  if (loading) {
    return (
      <div className="mt-8 flex items-center gap-2 text-sm text-gray-400">
        <Loader className="h-3.5 w-3.5 animate-spin" />
        Loading digest preview...
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-500">
        <Mail className="h-4 w-4 text-gray-400" />
        <span>We couldn't load your upcoming digest right now.</span>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          className="font-medium text-emerald-600 underline"
        >
          Retry
        </button>
      </div>
    );
  }
  if (!preview) return null;

  const docketsWithActivity = preview.dockets.filter(d => d.new_filings_count > 0);

  if (docketsWithActivity.length === 0) {
    return (
      <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700">Upcoming Digest</h3>
        </div>
        <p className="text-sm text-gray-500">
          No new filings since your last digest. When new filings are submitted on your watched
          dockets, they'll appear here.
        </p>
        {preview.next_digest_at && (
          <p className="mt-2 text-xs text-gray-400">
            Next digest: {new Date(preview.next_digest_at).toLocaleString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
            })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50/50 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-700">Upcoming Digest</h3>
        </div>
        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
          {preview.total_new_filings} new filing{preview.total_new_filings !== 1 ? 's' : ''}
        </span>
      </div>
      <p className="mb-3 text-xs text-gray-500">
        This is what your next digest email will contain.
      </p>
      <div className="space-y-2">
        {docketsWithActivity.map((d) => {
          const topFilers = Array.isArray(d.top_filers) ? d.top_filers : [];
          return (
            <div
              key={d.docket_number}
              className="flex items-start justify-between gap-3 rounded-lg border border-emerald-200/60 bg-white p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                  <span className="text-sm font-medium text-gray-900">{d.docket_number}</span>
                </div>
                {d.proceeding_name && (
                  <p className="mt-0.5 ml-6 text-xs text-gray-500 truncate">{cleanProceedingName(d.proceeding_name)}</p>
                )}
                {topFilers.length > 0 && (
                  <p className="mt-1 ml-6 text-[11px] text-gray-400">
                    Top filers: {topFilers.join(', ')}
                  </p>
                )}
              </div>
              <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                +{d.new_filings_count}
              </span>
            </div>
          );
        })}
      </div>
      {preview.next_digest_at && (
        <p className="mt-3 text-xs text-gray-400">
          Next digest: {new Date(preview.next_digest_at).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          })}
        </p>
      )}
    </div>
  );
};

// ── Suggested Dockets ──────────────────────────────────────────────────────

interface SuggestedDocketsProps {
  followedNumbers: Set<string>;
  onFollow: (dn: string) => Promise<void>;
  actionLoading: string | null;
}

interface SuggestedProceeding {
  docket_number: string;
  proceeding_name: string;
  last_document_count: number | null;
}

const SuggestedDockets: React.FC<SuggestedDocketsProps> = ({
  followedNumbers,
  onFollow,
  actionLoading,
}) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<SuggestedProceeding[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const { data, error } = await supabase
          .from('open_proceedings')
          .select('docket_number, proceeding_name, last_document_count')
          .eq('status', 'open')
          .order('last_document_count', { ascending: false, nullsFirst: false })
          .limit(30);

        if (error) throw error;

        const filtered = (data || [])
          .filter(p => !followedNumbers.has(p.docket_number))
          .slice(0, 6);

        setSuggestions(filtered);
      } catch (err) {
        console.error('Error loading suggestions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestions();
  }, [followedNumbers]);

  if (loading || suggestions.length === 0) return null;

  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-4 w-4 text-amber-500" />
        <h2 className="text-sm font-semibold text-gray-700">
          Proceedings you might care about
        </h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {suggestions.map((proc) => (
          <div
            key={proc.docket_number}
            className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3 transition-shadow hover:shadow-sm"
          >
            <button
              onClick={() =>
                navigate(`/proceedings/${encodeURIComponent(proc.docket_number)}`)
              }
              className="min-w-0 text-left"
            >
              <span className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors">
                {proc.docket_number}
              </span>
              <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">
                {cleanProceedingName(proc.proceeding_name)}
              </p>
              {proc.last_document_count != null && proc.last_document_count > 0 && (
                <span className="mt-1 inline-block text-[10px] text-gray-400">
                  {proc.last_document_count.toLocaleString()} filings
                </span>
              )}
            </button>
            <button
              onClick={() => onFollow(proc.docket_number)}
              disabled={actionLoading === proc.docket_number}
              className="shrink-0 flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50"
            >
              {actionLoading === proc.docket_number ? (
                <Loader className="h-3 w-3 animate-spin" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              Follow
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
